import React, { forwardRef, memo, useCallback, useImperativeHandle, useMemo } from 'react';
import { Button } from '@Sage/hoi-poi-ui';
import { DataGrid } from '@Sage/ssm-web5';

import NoData from 'components/SvgIcons/emptyScreen/NoData';
import CustomEmptyView from 'containers/components/CustomEmptyView';
import { getLiteral } from 'utils/getLiteral';

import { LEADS_LITERALS } from './literals';

function getCompanyDisplayValue(data) {
    const name = data?.data?.name || data?.sourceLead?.name || '';
    const parts = [];
    if (name) parts.push(name);
    if (data?.data?.potential_duplicate) {
        parts.push(getLiteral(LEADS_LITERALS.potentialDuplicate));
    }
    if (data?.error) parts.push(data.error);
    return parts.length ? parts.join(' · ') : '';
}

function getQualityDisplayValue(data) {
    const d = data?.data?.filledPct;
    return d ? `${d}% ${getLiteral(LEADS_LITERALS.fields)}` : '-';
}

const ReviewActionCell = memo(
    forwardRef((params, ref) => {
        const { data, onReviewCreate } = params;

        useImperativeHandle(ref, () => ({
            refresh: () => true,
        }));

        if (!data || data.status === 'failed' || data.status === 'created') {
            return null;
        }

        return (
            <div className="fm-leads-modal__review-cell">
                <Button type="secondary" size="small" onClick={() => onReviewCreate(data)}>
                    {getLiteral(LEADS_LITERALS.reviewAndCreate)}
                </Button>
            </div>
        );
    }),
);

const ReviewQueue = ({ rows, onReviewCreate }) => {
    const getInfoForTagStatus = useCallback((data) => {
        const labelKey =
            data?.status === 'failed'
                ? LEADS_LITERALS.enhancementFailedStatus
                : LEADS_LITERALS[data?.status] || data?.status;
        const value = labelKey ? getLiteral(labelKey) : '';

        let type = 'pending';
        if (data?.status === 'created') type = 'success';
        else if (data?.status === 'failed') type = 'error';

        return { value, type };
    }, []);

    const emptyViewProps = useCallback(
        () => ({
            title: getLiteral('error_notresultfound'),
            subtitle: getLiteral('cfm_label_empty_list'),
            icon: <NoData />,
        }),
        [],
    );

    const components = useMemo(
        () => ({
            ReviewActionCell,
        }),
        [],
    );

    const columnDefs = useMemo(
        () => [
            {
                colId: 'company',
                headerName: getLiteral(LEADS_LITERALS.company),
                headerComponent: 'headerTextCell',
                headerComponentParams: {
                    showTotal: true,
                },
                valueGetter: (params) => getCompanyDisplayValue(params.data),
                cellRenderer: 'textCell',
                cellRendererParams: {
                    align: 'left',
                },
                width: 280,
            },
            {
                colId: 'quality',
                headerName: getLiteral(LEADS_LITERALS.matchQuality),
                valueGetter: (params) => getQualityDisplayValue(params.data),
                cellRenderer: 'textCell',
                cellRendererParams: {
                    align: 'left',
                },
                width: 160,
            },
            {
                colId: 'status',
                headerName: getLiteral(LEADS_LITERALS.status),
                field: 'status',
                cellRenderer: 'tagCell',
                cellRendererParams: {
                    innerMethods: {
                        getInfoForTag: getInfoForTagStatus,
                    },
                },
                width: 180,
            },
            {
                colId: 'actions',
                headerName: getLiteral(LEADS_LITERALS.rowAction),
                field: 'actions',
                cellRenderer: 'ReviewActionCell',
                cellRendererParams: {
                    align: 'left',
                    onReviewCreate,
                },
                width: 180,
            },
        ],
        [getInfoForTagStatus, onReviewCreate],
    );

    if (!rows?.length) {
        return (
            <div className="fm-leads-modal__review-table fm-leads-modal__review-table--empty">
                <CustomEmptyView emptyViewProps={emptyViewProps} />
            </div>
        );
    }

    return (
        <div className="fm-leads-modal__review-table">
            <DataGrid
                id="leads-review-queue"
                columnDefs={columnDefs}
                rowData={rows}
                components={components}
                useSort={false}
                forceRowUpdating
            />
        </div>
    );
};

export default memo(ReviewQueue);
